/**
 * Always-on error and anomaly log.
 *
 * Entries are structured JSONL. Nothing here rewrites career data.
 * Secrets are stripped before an entry is stored.
 */

import { appNow } from "./appClock";

export const DIAGNOSTICS_FILE_CAP_BYTES = 10 * 1024 * 1024;
export const DIAGNOSTICS_FILE_KEEP = 5;
export const DIAGNOSTICS_BASENAME = "northstar-diagnostics";
export const DESK_SCHEMA_VERSION = 31;

export type DiagSeverity = "error" | "anomaly" | "warn";

export type DiagEntry = {
  timeUtc: string;
  timeLocal: string;
  version: string;
  careerId: string;
  module: string;
  severity: DiagSeverity;
  message: string;
  stack?: string;
  breadcrumb: string[];
  anomalyCode?: string;
};

export type DiagInput = {
  module: string;
  severity: DiagSeverity;
  message: string;
  stack?: string;
  anomalyCode?: string;
  now?: Date;
};

const BREADCRUMB_CAP = 20;
const BREADCRUMB_ON_ENTRY = 12;

let versionLabel = "";
let careerId = "none";
let breadcrumbs: string[] = [];
const memory: DiagEntry[] = [];
let unread = 0;
const seen = new Set<string>();
const listeners = new Set<(unread: number) => void>();

type Sink = (entry: DiagEntry) => void;
let sink: Sink | null = null;
let clearSink: (() => void) | null = null;

export function setDiagnosticContext(next: { version?: string; careerId?: string }): void {
  if (typeof next.version === "string") versionLabel = next.version;
  if (typeof next.careerId === "string" && next.careerId.trim()) careerId = next.careerId.trim();
}

export function diagnosticCareerId(input: { test?: boolean; id?: string; name?: string } | null | undefined): string {
  if (!input) return "none";
  const id = (input.id || input.name || "career").trim() || "career";
  return input.test === true ? `TEST:${id}` : `real:${id}`;
}

export function pushBreadcrumb(action: string): void {
  const text = redactSecrets(action).trim();
  if (!text) return;
  breadcrumbs = [...breadcrumbs, text].slice(-BREADCRUMB_CAP);
}

export function recentBreadcrumbs(): string[] {
  return breadcrumbs.slice(-BREADCRUMB_ON_ENTRY);
}

export function installDiagnosticSink(next: Sink | null): void {
  sink = next;
}

export function installDiagnosticClear(next: (() => void) | null): void {
  clearSink = next;
}

export function diagnosticEntries(): readonly DiagEntry[] {
  return memory;
}

export function unreadDiagnosticCount(): number {
  return unread;
}

export function subscribeDiagnostics(listener: (unread: number) => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function notify(): void {
  for (const listener of listeners) listener(unread);
}

export function formatLocalStamp(now: Date): string {
  const pad = (n: number, w = 2) => String(Math.trunc(Math.abs(n))).padStart(w, "0");
  const offMin = -now.getTimezoneOffset();
  const sign = offMin >= 0 ? "+" : "-";
  const hh = pad(Math.trunc(Math.abs(offMin) / 60));
  const mm = pad(Math.abs(offMin) % 60);
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())} ${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())} ${sign}${hh}${mm}`;
}

export function formatDiagLine(entry: DiagEntry): string {
  return JSON.stringify(entry);
}

export function parseDiagLine(line: string): DiagEntry | null {
  try {
    const parsed = JSON.parse(line) as Partial<DiagEntry>;
    if (!parsed || typeof parsed.message !== "string" || typeof parsed.module !== "string") return null;
    if (parsed.severity !== "error" && parsed.severity !== "anomaly" && parsed.severity !== "warn") return null;
    return {
      timeUtc: typeof parsed.timeUtc === "string" ? parsed.timeUtc : "",
      timeLocal: typeof parsed.timeLocal === "string" ? parsed.timeLocal : "",
      version: typeof parsed.version === "string" ? parsed.version : "",
      careerId: typeof parsed.careerId === "string" ? parsed.careerId : "none",
      module: parsed.module,
      severity: parsed.severity,
      message: parsed.message,
      stack: typeof parsed.stack === "string" ? parsed.stack : undefined,
      breadcrumb: Array.isArray(parsed.breadcrumb) ? parsed.breadcrumb.filter((row) => typeof row === "string") : [],
      anomalyCode: typeof parsed.anomalyCode === "string" ? parsed.anomalyCode : undefined,
    };
  } catch {
    return null;
  }
}

const SECRET_ASSIGN =
  /\b(api[_-]?key|token|secret|password|authorization|bearer|pin(?:hash|salt)?|github_pat|xai[_-]?key)\b\s*[:=]\s*["']?[^\s"',}]+/gi;
const SECRET_TOKEN =
  /\b(?:sk-[A-Za-z0-9_\-]{6,}|xai-[A-Za-z0-9_\-]{6,}|ghp_[A-Za-z0-9]{8,}|github_pat_[A-Za-z0-9_]{8,}|Bearer\s+[A-Za-z0-9._\-]{6,})/g;
const HOPPIE_CODE = /\b((?:hoppie(?:\s+logon)?|logon)\s*[:=]\s*)([A-Za-z0-9]{4,16})\b/gi;

/** Strip credentials, tokens, and Hoppie logon codes. The replacement is stable. */
export function redactSecrets(value: string): string {
  return value
    .replace(SECRET_TOKEN, "[redacted]")
    .replace(SECRET_ASSIGN, (_full, name: string) => `${name}=[redacted]`)
    .replace(HOPPIE_CODE, (_full, prefix: string) => `${prefix}[redacted]`);
}

export function recordDiagnostic(input: DiagInput): DiagEntry | null {
  const message = redactSecrets(input.message).slice(0, 2000);
  const stack = input.stack ? redactSecrets(input.stack).slice(0, 4000) : undefined;
  const key = `${input.severity}|${input.module}|${input.anomalyCode ?? ""}|${message}`;
  if (seen.has(key)) return null;
  seen.add(key);
  const now = input.now ?? appNow();
  const entry: DiagEntry = {
    timeUtc: now.toISOString(),
    timeLocal: formatLocalStamp(now),
    version: versionLabel,
    careerId,
    module: input.module,
    severity: input.severity,
    message,
    stack,
    breadcrumb: recentBreadcrumbs(),
    anomalyCode: input.anomalyCode,
  };
  memory.push(entry);
  if (memory.length > 2000) memory.splice(0, memory.length - 2000);
  if (entry.severity === "error" || entry.severity === "anomaly") unread += 1;
  try {
    sink?.(entry);
  } catch {
    /* a broken sink must not take the desk down */
  }
  notify();
  return entry;
}

export function acknowledgeDiagnostics(): void {
  unread = 0;
  notify();
}

export function clearDiagnostics(): void {
  memory.splice(0, memory.length);
  unread = 0;
  seen.clear();
  try {
    clearSink?.();
  } catch {
    /* clear stays local even if the file sink fails */
  }
  notify();
}

export function hydrateDiagnostics(entries: DiagEntry[]): void {
  for (const entry of entries) {
    const key = `${entry.severity}|${entry.module}|${entry.anomalyCode ?? ""}|${entry.message}`;
    if (seen.has(key)) continue;
    seen.add(key);
    memory.push(entry);
  }
}

export type Anomaly = { code: string; message: string };

export type DeskCheckInput = {
  fuelLb?: Array<number | null | undefined>;
  pax?: Array<{ count?: number | null; seats?: number | null; id?: string }>;
  duty?: Array<{ flightMin?: number | null; fdpMin?: number | null; flightLimit?: number | null; fdpLimit?: number | null; crewId?: string }>;
  balance?: number | null;
  ledgerAmounts?: number[];
  ledgerKeys?: string[];
  missions?: Array<{ id: string; aircraftId?: string }>;
  fleetIds?: string[];
  crewPayIds?: string[];
  times?: Array<{ id?: string; blockMin?: number | null; airMin?: number | null }>;
  schemaVersion?: number | null;
  expectedSchema?: number | null;
};

/** Read-only. Never mutates `input`. An anomaly is a log line. It does not stop sync, sign-in, or a wipe. */
export function checkDeskInvariants(input: DeskCheckInput): Anomaly[] {
  const out: Anomaly[] = [];
  for (const fuel of input.fuelLb ?? []) {
    if (fuel == null) continue;
    if (!Number.isFinite(fuel) || fuel < 0 || !Number.isInteger(fuel)) {
      out.push({
        code: "fuel",
        message: `Fuel ${fuel} lb is negative or not a whole pound.`,
      });
    }
  }
  for (const row of input.pax ?? []) {
    if (row.count == null || row.seats == null) continue;
    if (row.count > row.seats) {
      out.push({
        code: "pax",
        message: `Pax ${row.count} exceeds seats ${row.seats}${row.id ? ` on ${row.id}` : ""}.`,
      });
    }
  }
  for (const row of input.duty ?? []) {
    const flightOver =
      row.flightMin != null && row.flightLimit != null && row.flightMin > row.flightLimit;
    const fdpOver = row.fdpMin != null && row.fdpLimit != null && row.fdpMin > row.fdpLimit;
    if (flightOver || fdpOver) {
      out.push({
        code: "duty",
        message: `Crew ${row.crewId ?? "duty"} is outside the duty limit (flight ${row.flightMin ?? "—"} / ${row.flightLimit ?? "—"} min, FDP ${row.fdpMin ?? "—"} / ${row.fdpLimit ?? "—"} min).`,
      });
    }
  }
  if (input.balance != null && input.ledgerAmounts) {
    const sum = input.ledgerAmounts.reduce((total, amount) => total + amount, 0);
    if (Math.round(sum * 100) !== Math.round(input.balance * 100)) {
      out.push({
        code: "ledger",
        message: `Ledger lines sum to ${sum} but the balance is ${input.balance}.`,
      });
    }
  }
  const keys = input.ledgerKeys ?? [];
  const keySeen = new Set<string>();
  for (const key of keys) {
    if (!key) continue;
    if (keySeen.has(key)) {
      out.push({ code: "ledger-key", message: `Duplicate ledger key ${key}.` });
    }
    keySeen.add(key);
  }
  const paySeen = new Set<string>();
  for (const id of input.crewPayIds ?? []) {
    if (!id) continue;
    if (paySeen.has(id)) out.push({ code: "crew-pay", message: `Duplicate crew-pay key ${id}.` });
    paySeen.add(id);
  }
  const fleet = new Set(input.fleetIds ?? []);
  if (fleet.size) {
    for (const mission of input.missions ?? []) {
      if (mission.aircraftId && !fleet.has(mission.aircraftId)) {
        out.push({ code: "orphan-trip", message: `Trip ${mission.id} points at missing aircraft ${mission.aircraftId}.` });
      }
    }
  }
  for (const row of input.times ?? []) {
    if (row.blockMin == null || row.airMin == null) continue;
    if (row.airMin > row.blockMin + 1) {
      out.push({
        code: "block-air",
        message: `Air time ${row.airMin} min is longer than block ${row.blockMin} min${row.id ? ` on ${row.id}` : ""}.`,
      });
    }
  }
  if (
    input.schemaVersion != null &&
    input.expectedSchema != null &&
    input.schemaVersion !== input.expectedSchema
  ) {
    out.push({
      code: "schema",
      message: `Saved schema ${input.schemaVersion} does not match this build (${input.expectedSchema}).`,
    });
  }
  return out;
}

export type LooseDesk = {
  career?: { test?: boolean; testId?: string; playMode?: string };
  company?: { id?: string; operatorName?: string };
  fleet?: Array<{ id?: string; maxPax?: number; registration?: string }>;
  missions?: Array<{ id?: string; aircraftId?: string; paxCount?: number; fuelLbs?: number; registration?: string }>;
  account?: { balanceCad?: number; transactions?: Array<{ amountCad?: number; idempotencyKey?: string }> };
  duty?: {
    settings?: { limits?: { maxFdpMin?: number; maxFlightTimeMin?: number } };
    sessions?: Array<{
      crewId?: string;
      status?: string;
      startedAtUtc?: string;
      flightTimeMin?: number;
      pauseAccumMin?: number;
      pausedAtUtc?: string;
    }>;
  };
  crewLedger?: Array<{ id?: string }>;
  flightLogs?: Array<{
    id?: string;
    departedAtUtc?: string;
    arrivedAtUtc?: string;
    arrival?: { engineHours?: number };
  }>;
  schemaVersion?: number;
};

function minutesBetween(startIso: string | undefined, end: Date): number | null {
  if (!startIso) return null;
  const start = Date.parse(startIso);
  if (!Number.isFinite(start)) return null;
  return Math.round((end.getTime() - start) / 60_000);
}

/** Build the check from a desk slice. Does not write. */
export function invariantsFromDesk(state: LooseDesk, now = appNow()): Anomaly[] {
  const fleet = state.fleet ?? [];
  const fleetIds = fleet.map((row) => row.id).filter((id): id is string => Boolean(id));
  const seats = new Map(fleet.filter((row) => row.id).map((row) => [row.id as string, row.maxPax]));
  const fuel = (state.missions ?? []).map((mission) => mission.fuelLbs);
  const pax = (state.missions ?? []).map((mission) => ({
    id: mission.id,
    count: mission.paxCount,
    seats: mission.aircraftId ? seats.get(mission.aircraftId) : undefined,
  }));
  const limits = state.duty?.settings?.limits;
  const duty = (state.duty?.sessions ?? [])
    .filter((session) => session.status === "on")
    .map((session) => {
      const elapsed = minutesBetween(session.startedAtUtc, now);
      const paused = session.pausedAtUtc ? minutesBetween(session.pausedAtUtc, now) : 0;
      const fdp = elapsed == null ? null : Math.max(0, elapsed - (session.pauseAccumMin ?? 0) - (paused ?? 0));
      return {
        crewId: session.crewId,
        flightMin: session.flightTimeMin,
        fdpMin: fdp,
        flightLimit: limits?.maxFlightTimeMin,
        fdpLimit: limits?.maxFdpMin,
      };
    });
  const txns = state.account?.transactions ?? [];
  const times = (state.flightLogs ?? []).map((log) => {
    const departed = log.departedAtUtc ? Date.parse(log.departedAtUtc) : NaN;
    const arrived = log.arrivedAtUtc ? Date.parse(log.arrivedAtUtc) : NaN;
    const block =
      Number.isFinite(departed) && Number.isFinite(arrived) && arrived > departed
        ? Math.round((arrived - departed) / 60_000)
        : null;
    const hours = log.arrival?.engineHours;
    const air =
      hours != null && Number.isFinite(hours) && hours >= 0 && hours <= 18
        ? Math.round(hours * 60)
        : null;
    return { id: log.id, blockMin: block, airMin: air };
  });
  return checkDeskInvariants({
    fuelLb: fuel,
    pax,
    duty,
    balance: state.account?.balanceCad,
    ledgerAmounts: txns.map((txn) => (typeof txn.amountCad === "number" ? txn.amountCad : 0)),
    ledgerKeys: txns.map((txn) => txn.idempotencyKey ?? "").filter(Boolean),
    missions: (state.missions ?? []).map((mission) => ({
      id: mission.id ?? "trip",
      aircraftId: mission.aircraftId,
    })),
    fleetIds,
    crewPayIds: (state.crewLedger ?? []).map((row) => row.id ?? "").filter(Boolean),
    times,
    schemaVersion: state.schemaVersion,
    expectedSchema: state.schemaVersion == null ? undefined : DESK_SCHEMA_VERSION,
  });
}

/** Record anomalies that were not already logged. Does not change the desk. */
export function noteDeskInvariants(state: LooseDesk, now = appNow()): DiagEntry[] {
  setDiagnosticContext({
    careerId: diagnosticCareerId({
      test: state.career?.test === true,
      id: state.career?.testId || state.company?.id,
      name: state.company?.operatorName,
    }),
  });
  const found = invariantsFromDesk(state, now);
  const recorded: DiagEntry[] = [];
  for (const anomaly of found) {
    const entry = recordDiagnostic({
      module: "invariants",
      severity: "anomaly",
      message: anomaly.message,
      anomalyCode: anomaly.code,
      now,
    });
    if (entry) recorded.push(entry);
  }
  return recorded;
}

export type RotationPlan = { action: "append" | "rotate"; fileName: string };

/** Decide whether the next line fits in the current file. */
export function planDiagRotation(currentBytes: number, incomingBytes: number, cap = DIAGNOSTICS_FILE_CAP_BYTES): RotationPlan {
  const fileName = `${DIAGNOSTICS_BASENAME}.jsonl`;
  if (currentBytes > 0 && currentBytes + incomingBytes > cap) return { action: "rotate", fileName };
  return { action: "append", fileName };
}

export function rotatedDiagName(index: number): string {
  return `${DIAGNOSTICS_BASENAME}.${index}.jsonl`;
}

function crc32(bytes: Uint8Array): number {
  let crc = 0xffffffff;
  for (const byte of bytes) {
    crc ^= byte;
    for (let bit = 0; bit < 8; bit += 1) {
      crc = crc & 1 ? (crc >>> 1) ^ 0xedb88320 : crc >>> 1;
    }
  }
  return (crc ^ 0xffffffff) >>> 0;
}

function u16(value: number): number[] {
  return [value & 0xff, (value >>> 8) & 0xff];
}

function u32(value: number): number[] {
  return [value & 0xff, (value >>> 8) & 0xff, (value >>> 16) & 0xff, (value >>> 24) & 0xff];
}

/** Uncompressed ZIP (method 0). Enough for a redacted bug-report bundle. */
export function zipStore(files: { name: string; text: string }[], now = new Date()): Uint8Array {
  const locals: number[] = [];
  const central: number[] = [];
  let offset = 0;
  const dosTime = ((now.getHours() & 31) << 11) | ((now.getMinutes() & 63) << 5) | ((Math.floor(now.getSeconds() / 2)) & 31);
  const dosDate = (((now.getFullYear() - 1980) & 127) << 9) | (((now.getMonth() + 1) & 15) << 5) | (now.getDate() & 31);
  for (const file of files) {
    const name = file.name.replace(/\\/g, "/");
    const nameBytes = Array.from(new TextEncoder().encode(name));
    const data = new TextEncoder().encode(redactSecrets(file.text));
    const crc = crc32(data);
    const local = [
      ...u32(0x04034b50),
      ...u16(20),
      ...u16(0),
      ...u16(0),
      ...u16(dosTime),
      ...u16(dosDate),
      ...u32(crc),
      ...u32(data.length),
      ...u32(data.length),
      ...u16(nameBytes.length),
      ...u16(0),
      ...nameBytes,
      ...data,
    ];
    const centralHeader = [
      ...u32(0x02014b50),
      ...u16(20),
      ...u16(20),
      ...u16(0),
      ...u16(0),
      ...u16(dosTime),
      ...u16(dosDate),
      ...u32(crc),
      ...u32(data.length),
      ...u32(data.length),
      ...u16(nameBytes.length),
      ...u16(0),
      ...u16(0),
      ...u16(0),
      ...u16(0),
      ...u32(0),
      ...u32(offset),
      ...nameBytes,
    ];
    locals.push(...local);
    central.push(...centralHeader);
    offset += local.length;
  }
  const centralOffset = offset;
  const end = [
    ...u32(0x06054b50),
    ...u16(0),
    ...u16(0),
    ...u16(files.length),
    ...u16(files.length),
    ...u32(central.length),
    ...u32(centralOffset),
    ...u16(0),
  ];
  return Uint8Array.from([...locals, ...central, ...end]);
}

const BROWSER_DIAG_KEY = "ns.dispatch.diagnostics";
const BROWSER_DIAG_CAP = 200_000;
const BROWSER_DIAG_KEEP = 5;

/** Browser and companion stand-in for the rotating app-data file. */
export function persistBrowserDiagnostic(line: string): void {
  if (typeof localStorage === "undefined") return;
  try {
    const current = localStorage.getItem(BROWSER_DIAG_KEY) ?? "";
    const plan = planDiagRotation(current.length, line.length + 1, BROWSER_DIAG_CAP);
    if (plan.action === "rotate") {
      for (let index = BROWSER_DIAG_KEEP; index >= 2; index -= 1) {
        const older = localStorage.getItem(`${BROWSER_DIAG_KEY}.${index - 1}`);
        if (older != null) localStorage.setItem(`${BROWSER_DIAG_KEY}.${index}`, older);
        else localStorage.removeItem(`${BROWSER_DIAG_KEY}.${index}`);
      }
      localStorage.setItem(`${BROWSER_DIAG_KEY}.1`, current);
      localStorage.setItem(BROWSER_DIAG_KEY, `${line}\n`);
      return;
    }
    localStorage.setItem(BROWSER_DIAG_KEY, current ? `${current}\n${line}` : line);
  } catch {
    /* the in-memory log still holds the entry */
  }
}

export function clearBrowserDiagnostics(): void {
  if (typeof localStorage === "undefined") return;
  try {
    localStorage.removeItem(BROWSER_DIAG_KEY);
    for (let index = 1; index <= BROWSER_DIAG_KEEP; index += 1) localStorage.removeItem(`${BROWSER_DIAG_KEY}.${index}`);
  } catch {
    /* ignore */
  }
}

let globalGuards = false;

/** Browser uncaught errors and rejected promises. Safe to call more than once. */
export function installGlobalDiagnostics(): void {
  if (globalGuards || typeof window === "undefined") return;
  globalGuards = true;
  window.addEventListener("error", (event) => {
    const error = event.error;
    recordDiagnostic({
      module: "window",
      severity: "error",
      message: event.message || (error instanceof Error ? error.message : "Uncaught error"),
      stack: error instanceof Error ? error.stack : undefined,
    });
  });
  window.addEventListener("unhandledrejection", (event) => {
    const reason = event.reason;
    recordDiagnostic({
      module: "promise",
      severity: "error",
      message: reason instanceof Error ? reason.message : String(reason ?? "Unhandled rejection"),
      stack: reason instanceof Error ? reason.stack : undefined,
    });
  });
}

export function summarizeDiagnostics(entries: readonly DiagEntry[]): string {
  const errors = entries.filter((entry) => entry.severity === "error");
  const anomalies = entries.filter((entry) => entry.severity === "anomaly");
  const lines = [
    `errors ${errors.length}`,
    `anomalies ${anomalies.length}`,
    `entries ${entries.length}`,
  ];
  const recent = entries.slice(-20);
  for (const entry of recent) {
    lines.push(`${entry.timeUtc} ${entry.severity} ${entry.module} ${entry.anomalyCode ?? ""} ${entry.message}`.trim());
  }
  return lines.join("\n");
}
